import { useEffect, type RefObject } from 'react';

/**
 * Makes a full-screen layer actually modal: while it is mounted, every other
 * child of <body> is `inert` and `aria-hidden`, and `focusRef` takes focus. So
 * neither a control that was focused underneath nor Tab can reach the page
 * behind it. `aria-modal` alone enforces none of this (Codex, #1084).
 *
 * The layer must be rendered straight into <body> (a portal), so that it is a
 * sibling of the app root rather than inside it. There is no Escape handling:
 * the two callers — the native update screen and the web reload prompt — have
 * no dismiss path by design. Elements that were already inert or hidden are
 * left as they were when the layer goes away. Children added to <body> later
 * are blocked as they arrive, and focus is taken back if it leaves the layer.
 */
export function useBlockingLayer(rootRef: RefObject<HTMLElement>, focusRef: RefObject<HTMLElement>) {
  useEffect(() => {
    const self = rootRef.current;
    const blocked: HTMLElement[] = [];
    const hidden: HTMLElement[] = [];
    const block = (el: Element) => {
      if (!(el instanceof HTMLElement) || el === self || el.hasAttribute('inert')) return;
      el.setAttribute('inert', '');
      blocked.push(el);
      if (!el.hasAttribute('aria-hidden')) {
        el.setAttribute('aria-hidden', 'true');
        hidden.push(el);
      }
    };
    const reclaimFocus = () => {
      if (!self?.contains(document.activeElement)) focusRef.current?.focus();
    };
    Array.from(document.body.children).forEach(block);
    focusRef.current?.focus();

    // Anything appended to <body> while the layer is up — a portal such as the
    // session-timeout AlertDialog — is blocked too, and focus comes back here
    // if it ever leaves (Codex, #1084).
    const observer = new MutationObserver((records) => {
      records.forEach((r) => r.addedNodes.forEach((n) => block(n as Element)));
      reclaimFocus();
    });
    observer.observe(document.body, { childList: true });
    document.addEventListener('focusin', reclaimFocus);

    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', reclaimFocus);
      blocked.forEach((el) => el.removeAttribute('inert'));
      hidden.forEach((el) => el.removeAttribute('aria-hidden'));
    };
  }, [rootRef, focusRef]);
}
